import { NavLink } from "react-router-dom";
import { Home, Package, Tag, LayoutTemplate, PenSquare, FolderClock, Settings, LogOut, Layers, LibraryBig, Languages, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/", key: "home", icon: Home, testId: "nav-home" },
  { to: "/products", key: "products", icon: Package, testId: "nav-products" },
  { to: "/brands", key: "brands", icon: Tag, testId: "nav-brands" },
  { to: "/templates", key: "templates", icon: LayoutTemplate, testId: "nav-templates" },
  { to: "/designer", key: "designer", icon: PenSquare, testId: "nav-designer" },
  { to: "/standard-cards", key: "standardCards", icon: LibraryBig, testId: "nav-standard-cards" },
  { to: "/batch", key: "batch", icon: Layers, testId: "nav-batch" },
  { to: "/drafts", key: "drafts", icon: FolderClock, testId: "nav-drafts" },
  { to: "/help", key: "help", icon: HelpCircle, testId: "nav-help" },
  { to: "/settings", key: "settings", icon: Settings, testId: "nav-settings" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;

  const setLang = (l) => i18n.changeLanguage(l);

  return (
    <aside className="bhf-sidebar flex h-screen w-60 flex-col shrink-0" data-testid="sidebar">
      <div className="px-6 pt-7 pb-6">
        <div className="font-display text-2xl font-800 text-white tracking-tight">BHF</div>
        <div className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-slate-400">{t("nav.appName")}</div>
      </div>
      <nav className="flex-1 space-y-1 px-3 bhf-scrollbar overflow-y-auto">
        {NAV_ITEMS.map(({ to, key, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testId}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                isActive ? "bg-white/10 text-white shadow-[inset_2px_0_0_0_#818cf8]" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {t(`nav.${key}`)}
          </NavLink>
        ))}
      </nav>

      <div className="px-4 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-white/5 p-1" data-testid="language-switcher">
          <Languages className="ml-1.5 h-3.5 w-3.5 text-slate-400" />
          {[["en", t("language.en")], ["zh", t("language.zh")]].map(([code, label]) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              data-testid={`lang-${code}`}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                lang === code ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2 border-t border-white/10 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-xs font-semibold text-white">
              {(user?.name || "U").charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-slate-200" data-testid="sidebar-user-name">{user?.name || "User"}</div>
              <div className="truncate text-[10px] text-slate-500">{user?.role === "admin" ? t("settings.roleAdmin") : t("settings.roleUser")}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="rounded-md p-1.5 text-slate-500 hover:bg-white/10 hover:text-white transition-colors"
            title={t("nav.logout")}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
